import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Spinner, Alert, Button, Pagination } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import { useAuth } from '../../context/AuthContext';
import colors from '../../constants/colors';
import { formatPrice } from '../../utils/formatPrice';

const MyOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if not logged in
    if (!user) {
      navigate('/login', { state: { from: '/minhas-encomendas' } });
      return;
    }

    fetchOrders();
  }, [user, navigate, currentPage]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/order/myorders');
      setOrders(response);
      
      // If pagination info is available
      if (response.pagination) {
        setTotalPages(response.pagination.totalPages);
      }
      
      setError('');
    } catch (err) {
      setError('Não foi possível carregar as tuas encomendas. Por favor tenta novamente mais tarde.');
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine badge variant based on order status
  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'Pendente', variant: 'warning' },
      'awaiting_payment': { text: 'A Aguardar Pagamento', variant: 'warning' },
      'processing': { text: 'Em Processamento', variant: 'info' },
      'shipped': { text: 'Enviada', variant: 'primary' },
      'delivered': { text: 'Entregue', variant: 'success' },
      'canceled': { text: 'Cancelada', variant: 'danger' },
      'payment_failed': { text: 'Pagamento Falhado', variant: 'danger' },
      'payment_initiation_failed': { text: 'Pagamento Não Iniciado', variant: 'danger' }
    };
    
    const defaultStatus = { text: status, variant: 'secondary' };
    return statusMap[status] || defaultStatus;
  };
  
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('pt-PT', options);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Render pagination controls
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let items = [];
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item 
          key={number} 
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="justify-content-center mt-4">
        <Pagination.Prev 
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        />
        {items}
        <Pagination.Next 
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    );
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" className="my-5">
          <span className="visually-hidden">A carregar...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="mb-4">
        <Col>
          <h1>Minhas Encomendas</h1>
          <p className="text-muted">Gerir e acompanhar todas as tuas encomendas</p>
        </Col>
      </Row>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      {orders.length === 0 ? (
        <Card className="text-center p-5 shadow-sm">
          <Card.Body>
            <h4>Não tem encomendas ainda</h4>
            <p className="text-muted">Explora a nossa loja e faz a tua primeira compra!</p>
            <Button as={Link} to="/produtos" className="mt-3" style={{ backgroundColor: colors.secondary, color: colors.light, borderColor: colors.secondary }}>
              Explorar Produtos
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Card className="shadow-sm">
            <Card.Body>
              <Table responsive hover className="align-middle">
                <thead style={{ backgroundColor: colors.background }}>
                  <tr>
                    <th>Nº Encomenda</th>
                    <th>Data</th>
                    <th>Total</th>
                    <th>Estado</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td><strong>#{order.id}</strong></td>
                      <td>{formatDate(order.order_date)}</td>
                      <td>{formatPrice(order.total_price_with_tax)}€</td>
                      <td>
                        <Badge bg={getStatusBadge(order.status).variant}>
                          {getStatusBadge(order.status).text}
                        </Badge>
                      </td>
                      <td>
                        <Button 
                          as={Link} 
                          to={`/minhas-encomendas/${order.id}`} 
                          size="sm"
                          style={{ backgroundColor: colors.secondary, color: colors.light, borderColor: colors.secondary }}
                        >
                          Ver Detalhes
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
          
          {renderPagination()}
        </>
      )}
    </Container>
  );
};

export default MyOrdersPage;
